import React from 'react';
import { money, POS_COMPANY_FOOTER } from '@/lib/posReceipt';

function Row({ label, value, strong, muted }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className={muted ? 'text-white/40' : 'text-white/70'}>{label}</span>
      <span className={strong ? 'text-white font-black' : 'text-white/80 font-mono'}>{value}</span>
    </div>
  );
}

/**
 * The on-screen VAT receipt card plus the monochrome print sheet. The print
 * block is hidden on screen and revealed by the @media print rules in index.css
 * when staff tap Print.
 */
export default function PosReceiptCard({ receipt, reference, paymentMethod = 'Square', email }) {
  return (
    <>
      <div className="border border-white/10 bg-[#0d0d0f]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <p className="text-white font-black text-xs tracking-widest uppercase">VAT Receipt</p>
          <p className="text-white/50 font-mono text-xs font-bold">#{reference}</p>
        </div>

        <div className="px-4 py-3 space-y-2 text-xs">
          {receipt.lines.map((l, i) => (
            <div key={i} className="flex items-start justify-between gap-4">
              <span className="text-white/70 min-w-0">
                {l.qty > 1 && <span className="text-primary font-bold mr-1.5">{l.qty}×</span>}
                {l.label}
              </span>
              <span className="text-white/80 font-mono shrink-0">{money(l.net)}</span>
            </div>
          ))}
        </div>

        <div className="px-4 py-3 space-y-1.5 text-xs border-t border-white/10">
          <Row label="Subtotal (net)" value={money(receipt.netTotal)} muted />
          <Row label={`VAT @ ${receipt.vatRatePct}%`} value={money(receipt.vatTotal)} muted />
          <div className="flex items-center justify-between gap-4 pt-2 border-t border-white/10">
            <span className="text-white font-black text-sm uppercase">Total paid</span>
            <span className="text-primary font-black text-xl">{money(receipt.grossTotal)}</span>
          </div>
          <Row label="Payment method" value={paymentMethod} muted />
          {email && <Row label="Receipt to" value={email} muted />}
        </div>

        <p className="px-4 py-3 border-t border-white/10 text-[10px] text-white/35 leading-relaxed">
          {POS_COMPANY_FOOTER}
        </p>
      </div>

      {/* Print sheet — receipt only, plain black on white. */}
      <div id="pos-receipt-print" className="hidden print:block">
        <div className="mx-auto max-w-[380px] text-black text-[12px] leading-relaxed">
          <p className="text-center font-bold text-[15px] tracking-widest uppercase">Motion Reality</p>
          <p className="text-center mb-4">VAT Receipt · #{reference}</p>

          <table className="w-full mb-3">
            <tbody>
              {receipt.lines.map((l, i) => (
                <tr key={i}>
                  <td className="align-top py-0.5">
                    {l.qty > 1 ? `${l.qty}× ` : ''}{l.label}
                  </td>
                  <td className="align-top py-0.5 text-right whitespace-nowrap">{money(l.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="w-full border-t border-black pt-1">
            <tbody>
              <tr><td className="py-0.5">Subtotal (net)</td><td className="py-0.5 text-right">{money(receipt.netTotal)}</td></tr>
              <tr><td className="py-0.5">VAT @ {receipt.vatRatePct}%</td><td className="py-0.5 text-right">{money(receipt.vatTotal)}</td></tr>
              <tr className="font-bold">
                <td className="py-1 border-t border-black">Total paid</td>
                <td className="py-1 border-t border-black text-right">{money(receipt.grossTotal)}</td>
              </tr>
              <tr><td className="py-0.5">Payment method</td><td className="py-0.5 text-right">{paymentMethod}</td></tr>
              {email && (
                <tr><td className="py-0.5">Receipt to</td><td className="py-0.5 text-right">{email}</td></tr>
              )}
            </tbody>
          </table>

          <p className="mt-6 text-[10px] text-center">{POS_COMPANY_FOOTER}</p>
          <p className="mt-2 text-[10px] text-center">Thank you — see you on the grid.</p>
        </div>
      </div>
    </>
  );
}